import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import type { EventDto, FeedDto } from '../api/types.js';
import { eventsApi } from '../api/events.js';
import { Tag } from './Tag.js';
import { formatRelative } from '../lib/format.js';
import { feedSourceLabel } from '../lib/labels.js';
import { qk } from '../lib/queryKeys.js';

/**
 * 最新竞赛资讯：聚合所有赛事的 timeline 动态（同一 queryKey 与赛事卡/详情页共享缓存，不会重复请求）。
 * 按发布时间倒序取前 10 条，每条带来源标签与所属赛事入口——不用点进赛事就能看到资讯。
 * 赛事联动页与首页仪表盘共用。
 */
export function LatestFeeds({ events, limit = 10 }: { events: EventDto[]; limit?: number }) {
  const timelineQueries = useQueries({
    queries: events.map((event) => ({
      queryKey: qk.eventTimeline(event.code),
      queryFn: () => eventsApi.timeline(event.code),
      staleTime: 60_000,
    })),
  });

  const merged: { feed: FeedDto; event: EventDto }[] = [];
  for (let i = 0; i < events.length; i += 1) {
    const event = events[i];
    if (!event) continue;
    const feeds = timelineQueries[i]?.data?.feeds ?? [];
    for (const feed of feeds) merged.push({ feed, event });
  }
  merged.sort((a, b) => (a.feed.publishedAt < b.feed.publishedAt ? 1 : -1));

  if (merged.length === 0) {
    return (
      <p className="muted small">
        暂无同步动态。系统每 30 分钟从各校官网与赛事官网抓取竞赛公告，命中「挑战杯」等关键词会自动收录到这里。
      </p>
    );
  }

  return (
    <div className="stack-sm">
      {merged.slice(0, limit).map(({ feed, event }) => (
        <div className="row-tight" key={feed.id}>
          <Tag tone="muted">{feedSourceLabel(feed.source)}</Tag>
          {feed.url.length > 0 ? (
            <a href={feed.url} target="_blank" rel="noreferrer" className="small wrap-anywhere">
              {feed.title}
            </a>
          ) : (
            <span className="small wrap-anywhere">{feed.title}</span>
          )}
          <Link to={`/events/${event.code}`} className="muted small nowrap">
            {event.name}
          </Link>
          <span className="muted small right">{formatRelative(feed.publishedAt)}</span>
        </div>
      ))}
    </div>
  );
}
